import { useEffect, useRef, useState } from "react";
import { Phone, Plus, Check, Wifi, Loader2 } from "lucide-react";
import { api, errorText } from "@/lib/api";
import { kindOf } from "./Directions";

const CORRIDORS = [2, 5, 10];
export const telHref = (p) => `tel:${String(p).replace(/[^\d+]/g, "")}`;

export function useAlong(nav, on, corridor) {
  const [state, setState] = useState({ results: [], busy: false, err: "" });
  const r = nav.route;
  const key = on && r ? `${nav.dest?.id}|${nav.via.map((v) => v.id).join(",")}|${corridor}|${r.from.lat.toFixed(2)},${r.from.lon.toFixed(2)}` : "";
  const routeRef = useRef(r);
  routeRef.current = r;
  useEffect(() => {
    const r = routeRef.current;
    if (!key || !r) { setState({ results: [], busy: false, err: "" }); return undefined; }
    let dead = false;
    const step = Math.max(1, Math.ceil(r.coords.length / 150));
    const coords = r.coords.filter((_, i) => i % step === 0);
    if (coords[coords.length - 1] !== r.coords[r.coords.length - 1]) coords.push(r.coords[r.coords.length - 1]);
    setState((s) => ({ ...s, busy: true, err: "" }));
    api.post("/driver/along", { coords, corridor_km: corridor })
      .then(({ data }) => { if (!dead) setState({ results: data.results, busy: false, err: "", sources: data.sources }); })
      .catch((e) => { if (!dead) setState({ results: [], busy: false, err: errorText(e, "Could not find stops along the route.") }); });
    return () => { dead = true; };
  }, [key, corridor]);
  return state;
}

export function AlongList({ along, nav, corridor, setCorridor, onPick }) {
  const ids = new Set(nav.via.map((v) => v.id));
  return (
    <div data-testid="drv-along">
      <div className="flex items-center gap-1.5 px-4 py-2">
        <span className="flex-1 text-xs text-slate-500" data-testid="drv-along-count">{along.busy ? "Finding stops along your route…" : `${along.results.length} stops within`}</span>
        {CORRIDORS.map((k) => <button key={k} data-testid={`drv-along-corridor-${k}`} onClick={() => setCorridor(k)} className={`rounded-full px-2.5 py-1 font-mono text-[11px] font-semibold ${corridor === k ? "bg-[#0A234A] text-white" : "bg-slate-100 text-slate-600"}`}>{k} km</button>)}
      </div>
      {along.err && <p className="border-y border-red-100 bg-red-50 px-4 py-2 text-xs font-semibold text-red-600" data-testid="drv-along-error">{along.err}</p>}
      {along.busy && !along.results.length && <p className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></p>}
      <ul>
        {along.results.map((s) => {
          const [label, color] = kindOf(s.kind);
          const added = ids.has(s.id);
          return (
            <li key={s.id} className="flex items-start gap-3 border-b border-slate-100 px-4 py-3" data-testid={`drv-along-${s.id}`}>
              <span className="mt-1.5 h-2.5 w-2.5 flex-shrink-0 rounded-full" style={{ background: color }} />
              <button onClick={() => onPick(s)} className="min-w-0 flex-1 text-left" data-testid={`drv-along-open-${s.id}`}>
                <span className="block truncate text-sm font-semibold text-[#0A234A]">{s.name || label}</span>
                <span className="block font-mono text-[11px] text-slate-500">in {s.along_km} km · {s.off_route_km} km off route</span>
                {s.available != null && <span className="mt-1 inline-flex items-center gap-1 rounded border border-emerald-200 bg-emerald-50 px-1.5 py-px font-mono text-[10px] font-bold text-emerald-700"><Wifi className="h-3 w-3" />{s.available} open</span>}
              </button>
              {s.phone && <a data-testid={`drv-along-call-${s.id}`} href={telHref(s.phone)} className="rounded-full border border-emerald-300 p-2 text-emerald-700 hover:bg-emerald-50" aria-label={`Call ${s.name || label}`}><Phone className="h-4 w-4" /></a>}
              <button data-testid={`drv-along-add-${s.id}`} disabled={added || nav.via.length >= 5} onClick={() => nav.addStop(s, s.along_km)} className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold ${added ? "bg-emerald-50 text-emerald-700" : "bg-[#0A234A] text-white disabled:opacity-40"}`}>{added ? <><Check className="h-3.5 w-3.5" />Added</> : <><Plus className="h-3.5 w-3.5" />Stop</>}</button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
